import React, { useState, useEffect, useCallback, useRef } from 'react';
import useImageLoader from '../hooks/useImageLoader';
import useColorExtractor from '../hooks/useColorExtractor';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';
import useSwipeNavigation from '../hooks/useSwipeNavigation';
import '../styles/Gallery.css';

const Gallery = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 'auto', height: 'auto' });
  const { images, loading, error } = useImageLoader();
  const dominantColor = useColorExtractor(images[currentImageIndex]);
  const autoRotateRef = useRef(null);
  const preloadedImagesRef = useRef(new Map());

  // Calculate inverse color for button (true color inversion)
  const getInverseColor = useCallback((color) => {
    if (!color) return '#ffffff';

    // For RGB colors like 'rgb(r, g, b)', extract and invert the values
    if (color.startsWith('rgb')) {
      const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (rgbMatch) {
        const r = 255 - parseInt(rgbMatch[1]);
        const g = 255 - parseInt(rgbMatch[2]);
        const b = 255 - parseInt(rgbMatch[3]);

        return `rgb(${r}, ${g}, ${b})`;
      }
    }

    // Fallback for other color formats - invert white/black
    if (color === '#ffffff' || color === '#fff') return '#000000';
    if (color === '#000000' || color === '#000') return '#ffffff';

    // Default fallback
    return '#ffffff';
  }, []);

  const buttonColor = getInverseColor(dominantColor);

  // Function to preload images
  const preloadImage = useCallback((imageSrc) => {
    if (!imageSrc || preloadedImagesRef.current.has(imageSrc)) return;

    const img = new Image();
    img.onload = () => {
      preloadedImagesRef.current.set(imageSrc, img);
    };
    img.src = imageSrc;
  }, []);

  // Preload next few images when current image changes
  useEffect(() => {
    if (images.length === 0) return;

    // Clear old preloaded images (keep only recent ones)
    if (preloadedImagesRef.current.size > 10) {
      const keysToDelete = Array.from(preloadedImagesRef.current.keys()).slice(0, 5);
      keysToDelete.forEach(key => preloadedImagesRef.current.delete(key));
    }

    // Preload next 3 images (current + 2 ahead)
    for (let i = 0; i < 3; i++) {
      const nextIndex = (currentImageIndex + i) % images.length;
      preloadImage(images[nextIndex]);
    }
  }, [images, currentImageIndex, preloadImage]);

  // Function to start/reset auto-rotation timer
  const startAutoRotate = useCallback(() => {
    // Clear existing timer
    if (autoRotateRef.current) {
      clearInterval(autoRotateRef.current);
    }

    // Only start timer if not paused
    if (!isPaused) {
      autoRotateRef.current = setInterval(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setCurrentImageIndex((prev) => (prev + 1) % images.length);
          setIsTransitioning(false);
        }, 600);
      }, 15000); // 15 seconds
    }
  }, [images.length, isPaused]);

  const nextImage = useCallback(() => {
    // Reset auto-rotation timer on manual navigation
    startAutoRotate();

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
      setIsTransitioning(false);
    }, 600); // Half of transition duration (1.2s / 2)
  }, [images.length, startAutoRotate]);

  // Check if next image is preloaded
  const isNextImagePreloaded = useCallback((nextIndex) => {
    return preloadedImagesRef.current.has(images[nextIndex]);
  }, [images]);

  // Toggle pause/play
  const togglePausePlay = useCallback(() => {
    setIsPaused(prev => {
      const newPaused = !prev;
      if (newPaused) {
        // Pausing - clear the timer
        if (autoRotateRef.current) {
          clearInterval(autoRotateRef.current);
          autoRotateRef.current = null;
        }
      } else {
        // Resuming - restart the timer
        startAutoRotate();
      }
      return newPaused;
    });
  }, [startAutoRotate]);

  const prevImage = useCallback(() => {
    // Reset auto-rotation timer on manual navigation
    startAutoRotate();

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
      setIsTransitioning(false);
    }, 600);
  }, [images.length, startAutoRotate]);

  // Calculate optimal image dimensions based on aspect ratio
  const calculateDimensions = useCallback(() => {
    if (!images[currentImageIndex]) return;

    const img = new Image();
    img.onload = () => {
      const { naturalWidth, naturalHeight } = img;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate aspect ratios
      const imageAspectRatio = naturalWidth / naturalHeight;
      const viewportAspectRatio = viewportWidth / viewportHeight;

      let dimensions = { width: 'auto', height: 'auto' };

      // If image is wider than viewport aspect ratio, constrain to 90% width
      // If image is taller than viewport aspect ratio, constrain to 90% height
      // This maintains 5% margins on all sides
      if (imageAspectRatio > viewportAspectRatio) {
        // Landscape image - take 90% width (5% margin on each side)
        dimensions.width = '90vw';
        dimensions.height = 'auto';
      } else {
        // Portrait image - take 90% height (5% margin on top/bottom)
        dimensions.width = 'auto';
        dimensions.height = '90vh';
      }

      setImageDimensions(dimensions);
    };

    img.src = images[currentImageIndex];
  }, [images, currentImageIndex]);

  useEffect(() => {
    calculateDimensions();

    // Recalculate on window resize
    const handleResize = () => {
      calculateDimensions();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateDimensions]);


  // Set random initial image when images are first loaded
  useEffect(() => {
    if (images.length > 0 && currentImageIndex === 0) {
      const randomIndex = Math.floor(Math.random() * images.length);
      setCurrentImageIndex(randomIndex);
    }
  }, [images.length, currentImageIndex]);

  // Start auto-rotation when component mounts or images change
  useEffect(() => {
    if (images.length > 1 && !isPaused) {
      startAutoRotate();
    }

    // Cleanup on unmount
    return () => {
      if (autoRotateRef.current) {
        clearInterval(autoRotateRef.current);
      }
      // Clear preloaded images
      preloadedImagesRef.current.clear();
    };
  }, [images.length, startAutoRotate, isPaused]);

  useKeyboardNavigation(nextImage, prevImage);
  useSwipeNavigation(nextImage, prevImage);

  if (loading) {
    return (
      <div className="gallery-container loading">
        <div className="loading-spinner">Loading images...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-container error">
        <div className="error-message">Error loading images: {error}</div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="gallery-container empty">
        <div className="empty-message">
          No images found. Please add images to the src/images folder.
        </div>
      </div>
    );
  }

  return (
    <div
      className="gallery-container"
      style={{
        backgroundColor: dominantColor
      }}
    >
      {/* Blurred background image */}
      <div
        key={currentImageIndex} // Force re-render when image changes
        className="background-image"
        style={{
          backgroundImage: `url(${images[currentImageIndex]})`,
          backgroundColor: dominantColor
        }}
      />

      {/* Main image container */}
      <div className="image-container">
        <img
          src={images[currentImageIndex]}
          alt={`Gallery image ${currentImageIndex + 1}`}
          className={`gallery-image ${isTransitioning ? 'fade-out' : 'fade-in'}`}
          style={{
            width: imageDimensions.width,
            height: imageDimensions.height
          }}
          draggable="false"
        />
      </div>

      {/* Pause/Play button */}
      {images.length > 1 && (
        <button
          className={`pause-play-button ${isPaused ? 'playing' : ''}`}
          onClick={togglePausePlay}
          aria-label={isPaused ? 'Resume slideshow' : 'Pause slideshow'}
          style={{ color: buttonColor }}
        />
      )}
    </div>
  );
};

export default Gallery;
